import { Component, OnInit, OnDestroy, AfterViewInit, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CountdownTimerService, TimeRemaining } from './countdown-timer.service';
import { MaintenanceService } from '../../services/maintenance.service';
import { ApiService } from '../../services/api.service';
import { GalleryImage } from '../../models/file.model';

@Component({
  selector: 'app-maintenance-page',
  templateUrl: './maintenance-page.component.html',
  styleUrls: ['./maintenance-page.component.scss'],
  providers: [CountdownTimerService]
})
export class MaintenancePageComponent implements OnInit, OnDestroy, AfterViewInit {
  maintenanceEndDate: string = '';

  timeRemaining: TimeRemaining = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false
  };

  private destroy$ = new Subject<void>();

  // Loading state for images
  imagesLoaded = false;
  loadedImageCount = 0;

  socialLinks = {
    instagram: 'https://www.instagram.com/iswint.ro',
    facebook: 'https://facebook.com/iswint.ro',
    tiktok: 'https://www.tiktok.com/@iswint.ro',
    twitter: 'https://twitter.com/iswint'
  };

  contactEmail = 'contact@iswint.ro';

  // Gallery images fetched from API
  eventImages: GalleryImage[] = [];
  showGallery = false;

  currentYear = new Date().getFullYear();

  constructor(
    private countdownService: CountdownTimerService,
    private maintenanceService: MaintenanceService,
    private apiService: ApiService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  async ngOnInit(): Promise<void> {
    const endDate = await this.maintenanceService.getMaintenanceEndDate();
    this.maintenanceEndDate = endDate || '';

    // Load gallery images from API
    await this.loadGalleryImages();

    if (isPlatformBrowser(this.platformId) && this.maintenanceEndDate) {
      this.countdownService.startCountdown(this.maintenanceEndDate)
        .pipe(takeUntil(this.destroy$))
        .subscribe((timeRemaining: TimeRemaining) => {
          this.timeRemaining = timeRemaining;
        });
    }
  }

  ngAfterViewInit(): void {
    // Preload images to ensure they're ready for the carousel
    if (isPlatformBrowser(this.platformId)) {
      this.preloadImages();
    }
  }

  private async loadGalleryImages(): Promise<void> {
    try {
      const files = await this.apiService.getMaintenanceGallery();

      if (files.length === 0) {
        this.showGallery = false;
        return;
      }

      this.eventImages = files.map(file => ({
        src: file.link,
        year: this.extractYear(file.name, file.description || '')
      }));

      // Sort by year descending (newest first)
      this.eventImages.sort((a, b) => {
        const yearA = parseInt(a.year) || 0;
        const yearB = parseInt(b.year) || 0;
        return yearB - yearA;
      });

      this.showGallery = true;
    } catch (error) {
      console.error('Failed to load gallery images:', error);
      this.showGallery = false;
    }
  }

  private extractYear(fileName: string, description: string): string {
    // Try filename first: "maintenance_2025_photo.jpg" -> "2025"
    const fileNameMatch = fileName.match(/maintenance_(\d{4})/i);
    if (fileNameMatch) {
      return fileNameMatch[1];
    }

    // Fallback to description: "ISWinT 2024" or "2023 Event" -> year
    const descriptionMatch = description?.match(/(20[0-9]{2})/);
    return descriptionMatch ? descriptionMatch[1] : '';
  }

  private preloadImages(): void {
    const imagesToLoad = this.eventImages.length;

    if (imagesToLoad === 0) {
      this.imagesLoaded = true;
      return;
    }

    this.eventImages.forEach(imageData => {
      const img = new Image();
      img.onload = () => {
        this.loadedImageCount++;
        if (this.loadedImageCount === imagesToLoad) {
          this.imagesLoaded = true;
        }
      };
      img.onerror = () => {
        // Even if an image fails, continue with the carousel
        this.loadedImageCount++;
        if (this.loadedImageCount === imagesToLoad) {
          this.imagesLoaded = true;
        }
      };
      img.src = imageData.src;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  formatNumber(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }
}
